import React, { useState } from 'react';
import { InfoIcon, v2 } from '@fieldnation/platform-components';
import styles from './styles.scss';
import PartsProviderInputs from './PartsProviderInputs';
import { AdditionalPartField, PartsProviderInputsValue } from './types.d';
import {
  additionalPartFieldsToProviderInputs,
  providerInputsToAdditionalPartFields,
} from './utils';

const { Checkbox } = v2;

const DEFAULT_PROVIDER_INPUTS: PartsProviderInputsValue = {
  rmaNumber: false,
  serialNumber: false,
  internalNumber: false,
  internalNumberAlias: '',
};

interface DeliveryRequirement {
  value: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

interface Props {
  /** Currently configured provider-required fields, e.g. `work_order.parts.additional_part_fields`. */
  fields: AdditionalPartField[];
  onChange: (fields: AdditionalPartField[]) => void;
  /** Renders the "Provider must add additional info" toggle and its inputs. Defaults to true. */
  showProviderInputs?: boolean;
  /** When provided, renders the "Parts must be delivered before arrival" toggle above the provider inputs. */
  deliveryRequirement?: DeliveryRequirement;
  /** Disables every checkbox/input in the panel, e.g. once parts usage has already been submitted. */
  disabled?: boolean;
}

const PartsProviderSettings = ({
  fields, onChange, showProviderInputs = true, deliveryRequirement, disabled = false,
}: Props): JSX.Element | null => {
  const [providerInputs, setProviderInputs] = useState<PartsProviderInputsValue>(
    additionalPartFieldsToProviderInputs(fields),
  );
  // Independent of `fields` so the panel can stay open while every checkbox is unchecked.
  const [providerMustAddInfo, setProviderMustAddInfo] = useState(fields.length > 0);

  if (!showProviderInputs && !deliveryRequirement) {
    return null;
  }

  const handleProviderInputsChange = (nextInputs: PartsProviderInputsValue) => {
    setProviderInputs(nextInputs);
    onChange(providerInputsToAdditionalPartFields(nextInputs));
  };

  return (
    <div className={styles.settingsContainer}>
      <p className={styles.settingsLabel}>Settings</p>
      {deliveryRequirement && (
        <div className={styles.settingOption}>
          <Checkbox
            id="shouldDeliverBeforeArrival"
            onChange={(selected) => {
              const isChecked = selected?.includes?.('shouldDeliverBeforeArrival') ?? false;
              deliveryRequirement.onChange(isChecked);
            }}
            options={[{
              label: 'Parts must be delivered before arrival',
              value: 'shouldDeliverBeforeArrival',
              disabled: disabled || deliveryRequirement.disabled,
            }]}
            value={deliveryRequirement.value ? ['shouldDeliverBeforeArrival'] : []}
          />
          <InfoIcon>
            The assigned provider will receive standardized instructions and reminders to
            confirm parts delivery before heading to site.
          </InfoIcon>
        </div>
      )}
      {showProviderInputs && (
        <div className={styles.settingOption}>
          <Checkbox
            id="providerMustAddInfo"
            onChange={(selected) => {
              const isChecked = selected?.includes?.('providerMustAddInfo') ?? false;
              setProviderMustAddInfo(isChecked);
              if (!isChecked) {
                handleProviderInputsChange({ ...DEFAULT_PROVIDER_INPUTS });
              }
            }}
            options={[{
              label: 'Provider must add additional info',
              value: 'providerMustAddInfo',
              disabled,
            }]}
            value={providerMustAddInfo ? ['providerMustAddInfo'] : []}
          />
          <InfoIcon>
            Require the assigned provider to submit additional identifiers for each part when entering part usage.
          </InfoIcon>
        </div>
      )}
      {showProviderInputs && providerMustAddInfo && (
        <PartsProviderInputs
          value={providerInputs}
          onChange={handleProviderInputsChange}
          disabled={disabled}
        />
      )}
    </div>
  );
};

PartsProviderSettings.defaultProps = {
  showProviderInputs: true,
  deliveryRequirement: undefined,
  disabled: false,
};

export default PartsProviderSettings;
